import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useImmer } from 'use-immer';
import { Form, Formik } from 'formik';
import { t } from 'ttag';
import { Alert, Button, Details, Loader, Price, Text } from 'components';
import * as Yup from 'yup';
import qs from 'qs';
import client from 'utils/client';
import { useCustomization } from 'hooks';
import {
  Backdrop,
  Body,
  ChoiceSelector,
  CustomizationQuestion,
  Header,
  Marker,
  Name,
  Number,
  PlayerFlag,
  RealTimePreview
} from './components';
import getObject from './utils/getObject';
import gaCustomizationAnalytics from './gaCustomizationAnalytics';
import { UPDATE_CART_DATA } from '../../cart/actions/actions';

function CustomizationOverlay({
  toggleVisible,
  showQuestion,
  productID,
  isPDP,
  lineID
}) {
  /**
   * The Customization Overlay needs to dynamically change its height depending on the Header.
   * We use a MutationObserver interface to check for changes to the Header and update the Overlay
   * height accordingly. See: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
   * */
  const [headerInfomation, updateHeaderInformation] = useImmer({
    visible: document.getElementById('fixed_header').style.position === 'fixed',
    height: document.getElementById('fixed_header').clientHeight
  });

  useEffect(() => {
    const observer = new MutationObserver(mutations => {
      const headerPosition = mutations[0].target.style.position;
      if ((headerPosition === 'fixed') !== headerInfomation.visible) {
        updateHeaderInformation({
          visible: headerPosition === 'fixed',
          height: document.getElementById('fixed_header').clientHeight
        });
      }
    });
    const header = document.getElementById('fixed_header');

    observer.observe(header, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: false
    });
    return () => observer.disconnect();
  }, [headerInfomation]);

  /*
   * Customization State Management
   * */
  const [isCustomizationEnabled, toggleCustomization] = useState(!showQuestion);
  const [customizationOptions, updateCustomizationOptions] = useImmer({});

  /*
   * Real-Time Preview (RTP) State Management
   * */
  const [isRTPEnabled, toggleRTP] = useState(false);
  const [showRTPDisclaimer, toggleRTPDisclaimer] = useState(true);
  const [RTP, setRTP] = useImmer({});

  /*
   * Formik State Management
   * */
  const [formikValues, setFormikValues] = useState({});

  /*
   * The Product Image
   * */
  const [productImage, setProductImage] = useState(
    /** @type {string} */ undefined
  );

  /**
   * The text used for the Disclaimer.
   * @type {string}
   */
  const disclaimerText = useMemo(
    () =>
      t`When this product is tailored to your individual needs (clothing or footwear with embroidery) you agree that your right to return lapses, see Consumer Contracts, Section 18, 2. If there's any fault with the product, you will of course still be able to return as a claim.`,
    []
  );

  /**
   * We use the useSelector and useDispatch Redux Hooks to fetch and send information to Redux.
   */
  const { isMobile } = useSelector(state => state.general, shallowEqual);

  const dispatch = useDispatch();

  // Fetch the Customization Data using the useCustomization Custom SWR Hook.
  const {
    data,
    isLoading: isCustomizationDataLoading,
    error: customizationDataError
  } = useCustomization(productID);

  const customizationData = useMemo(
    () => (data ? data.data : undefined),
    [data]
  );

  const getProductImage = useCallback(() => {
    if (
      customizationData &&
      Array.isArray(customizationData) &&
      customizationData[0].customization_image
    ) {
      return customizationData[0].customization_image;
    }
    return undefined;
  }, [customizationData]);

  /**
   * Fetch the information needed for the RTP and Product Image.
   */
  useEffect(() => {
    /**
     * Todo: Currently, the RTP always selects the first Customization Option for the Name and
     * Number. In the future, the customer should be able to select what font to use.
     *
     * */
    if (customizationData && Array.isArray(customizationData)) {
      customizationData.forEach(customizationDatum => {
        // Make sure that we fetch the Customization Template object.
        if (customizationDatum.template_id) {
          const { customization_options: templateOptions } = customizationDatum;

          if (
            templateOptions.BNAM &&
            Array.isArray(templateOptions.BNAM) &&
            templateOptions.BNAM.length > 0 &&
            templateOptions.BNUM &&
            Array.isArray(templateOptions.BNUM) &&
            templateOptions.BNUM.length > 0
          ) {
            toggleRTP(true);

            const [nameOption, numberOption] = [
              templateOptions.BNAM[0],
              templateOptions.BNUM[0]
            ];

            // Load the Custom Fonts defined in the Customization Options and add them to the Document.
            if (nameOption.font) {
              const fontName = 'customNameFont';

              const nameFont = new FontFace(
                fontName,
                `url(${nameOption.font})`
              );

              nameFont.load().then(font => {
                document.fonts.add(font);
              });

              setRTP(draft => ({
                ...draft,
                BNAM: {
                  color: nameOption.font_color,
                  curved: nameOption.curved,
                  fontName,
                  size: nameOption.font_size
                }
              }));
            }

            if (numberOption.font) {
              const fontName = 'customNumberFont';

              const numberFont = new FontFace(
                fontName,
                `url(${numberOption.font})`
              );

              numberFont.load().then(font => {
                document.fonts.add(font);
              });

              setRTP(draft => ({
                ...draft,
                BNUM: {
                  color: numberOption.font_color,
                  curved: numberOption.curved,
                  fontName,
                  size: numberOption.font_size
                }
              }));
            }
          }
        }
      });
    }

    setProductImage(getProductImage());
  }, [customizationData]);

  useEffect(() => {
    /*
     * There are four different Customization Types: Name, Number, Player Flag and Marker.
     * Check the Customization Data for each of the Customization Types and save it to the Customization Options Array.
     *
     * Some Products (Shirts) can have a choice of two different Customization Options: Custom Name and Number or an Official Player Name.
     * */
    const customizationTypes = ['name', 'number', 'player_flag', 'marker'];

    if (customizationData) {
      const hasChoice = !!getObject(customizationData, 'type', 'choice');

      customizationTypes.forEach(customizationType => {
        const isVisible = !(
          ['name', 'number', 'player_flag'].includes(customizationType) &&
          hasChoice
        );

        const customizationOption = getObject(
          customizationData,
          'name',
          customizationType
        );

        if (customizationOption)
          updateCustomizationOptions(draft => ({
            ...draft,
            hasChoice,
            [customizationType]: { ...customizationOption, isVisible }
          }));
      });
    }
  }, [customizationData]);

  const closeOverlay = () => {
    return new Promise(resolve => {
      dispatch({ type: 'SELECTING_CUSTOMIZATION', payload: false });
      toggleVisible(false);
      resolve();
    });
  };

  const validationSchema = Yup.object().shape({
    name: customizationOptions.name
      ? Yup.string().max(
          customizationOptions.name.max_length,
          t`Please enter a text shorter than ${customizationOptions.name.max_length} characters`
        )
      : undefined,
    number: Yup.number()
      .integer(t`Please enter a valid player number`)
      .typeError(t`Please enter a valid player number`)
      .min(0, t`Please enter a valid player number`)
      .max(99, t`Please enter a valid player number`)
  });

  const handleSubmit = (values, isValid = true) => {
    if (!isCustomizationEnabled) {
      dispatch({
        type: 'ADD_CUSTOMIZATION_LIST',
        customizationList: {},
        allData: customizationData
      });
    }

    if (isCustomizationEnabled && isValid) {
      const { name, number, player_flag, marker } = values;
      const customizationList = {};

      if (!name && !number && !player_flag && !marker) {
        return closeOverlay();
      }

      if (name) {
        customizationList.name = name;
        customizationList.print_type = 'custom';
      }
      if (number) {
        customizationList.number = number;
        customizationList.print_type = 'custom';
      }
      if (player_flag) {
        customizationList.player_flag = player_flag;
        customizationList.print_type = customizationOptions.hasChoice
          ? 'original'
          : 'custom';
      }
      if (marker) customizationList.marker = marker;

      if (isPDP) {
        dispatch({
          type: 'ADD_CUSTOMIZATION_LIST',
          customizationList,
          allData: customizationData
        });
      }

      if (!isPDP && lineID) {
        client
          .post(
            `/customizations/update/${lineID}/`,
            qs.stringify(customizationList)
          )
          .then(response => {
            gaCustomizationAnalytics(response);
            dispatch(UPDATE_CART_DATA());
          });
      }
    }

    if (isValid)
      return closeOverlay().then(() => {
        if (showQuestion) {
          const addToCartButton = document.getElementById('addToCart');
          if (addToCartButton) {
            addToCartButton.click();
          }
        }
      });

    return null;
  };

  return (
    <>
      <Backdrop closeOverlay={closeOverlay} />
      <Body headerInformation={headerInfomation} isMobile={isMobile}>
        <Header closeOverlay={closeOverlay} />

        <div className="mt-4 mb-8 space-y-4 px-4">
          {/* Real-Time Preview (RTP) */}
          <div className="relative flex justify-center">
            {productImage && isRTPEnabled && (
              <>
                <RealTimePreview
                  customizationData={customizationData}
                  formikValues={formikValues}
                  RTP={RTP}
                />
                {showRTPDisclaimer && (
                  <div className="absolute inset-x-0 bottom-0 z-20 flex flex-initial justify-center">
                    <Alert
                      variant="warning"
                      isClosable
                      onClose={() => toggleRTPDisclaimer(false)}
                    >
                      {t`The preview is indicative and the final result may vary.`}
                    </Alert>
                  </div>
                )}
              </>
            )}
            {productImage && (
              <img className="h-[300px] w-[300px]" src={productImage} alt="" />
            )}
          </div>
          {isCustomizationDataLoading && !customizationDataError ? (
            <div className="flex items-center justify-center">
              <Loader />
            </div>
          ) : (
            <>
              {/* Customization Question. */}
              {showQuestion && (
                <CustomizationQuestion
                  isCustomizationEnabled={isCustomizationEnabled}
                  toggleCustomization={toggleCustomization}
                />
              )}

              {/* Customization Form. */}
              {(isCustomizationEnabled && (
                <Formik
                  initialValues={{
                    player_flag: 0
                  }}
                  validationSchema={validationSchema}
                  onSubmit={() => {}}
                  innerRef={formikActions =>
                    formikActions
                      ? setFormikValues(formikActions.values)
                      : setFormikValues({})
                  }
                >
                  {({ setFieldValue, submitForm, values, isValid }) => (
                    <Form className="space-y-8">
                      {/* Choice Selector */}
                      {customizationOptions.hasChoice && (
                        <ChoiceSelector
                          customizationOptions={customizationOptions}
                          updateCustomizationOptions={
                            updateCustomizationOptions
                          }
                          setFieldValue={setFieldValue}
                        />
                      )}

                      {customizationOptions.player_flag &&
                        customizationOptions.player_flag.isVisible &&
                        customizationOptions.hasChoice && (
                          <Alert variant="info" isClosable={false}>
                            {customizationOptions.player_flag.title}
                          </Alert>
                        )}

                      {customizationOptions.name &&
                        customizationOptions.name.isVisible &&
                        customizationOptions.hasChoice && (
                          <Alert variant="info" isClosable={false}>
                            {customizationOptions.name.description}
                          </Alert>
                        )}

                      {/**
                       If a Number and Name are available at the same time with no Player Flag, then we will only show the Customization Price of the Name.
                       */}
                      {customizationOptions.name &&
                        customizationOptions.number &&
                        !customizationOptions.hasChoice && (
                          <div className="flex items-center justify-between">
                            <div className="w-4/5 flex-initial">
                              <Text>
                                {customizationOptions.name.description}
                              </Text>
                            </div>
                            <Price
                              unitPrice={customizationOptions.name.price}
                              priceSize="xs"
                              priceFontWeight="semibold"
                            />
                          </div>
                        )}

                      {/* Name */}
                      <Name
                        nameOptions={customizationOptions.name}
                        hidePrice={
                          !!(
                            customizationOptions.name &&
                            customizationOptions.number
                          ) || customizationOptions.hasChoice
                        }
                      />

                      {/* Number */}
                      <Number
                        numberOptions={customizationOptions.number}
                        hidePrice={
                          !!(
                            customizationOptions.name &&
                            customizationOptions.number
                          ) || customizationOptions.hasChoice
                        }
                      />

                      {/* Player Flag */}
                      <PlayerFlag
                        playerFlagOptions={customizationOptions.player_flag}
                        hasChoice={customizationOptions.hasChoice}
                      />

                      {/* Marker */}
                      <Marker markerOptions={customizationOptions.marker} />

                      <Button
                        type="submit"
                        onClick={() =>
                          submitForm().then(() => handleSubmit(values, isValid))
                        }
                        variant="secondary"
                        isFullWidth
                        data-testid="submit-customization-button"
                      >{t`Continue`}</Button>

                      <Details
                        summary={t`Cannot be returned when personalized`}
                        variant="warning"
                      >
                        {disclaimerText}
                      </Details>
                    </Form>
                  )}
                </Formik>
              )) || (
                <Button
                  type="button"
                  onClick={() => handleSubmit()}
                  variant="secondary"
                  isFullWidth
                  data-testid="submit-customization-button"
                >{t`Continue`}</Button>
              )}
            </>
          )}
        </div>
      </Body>
    </>
  );
}

CustomizationOverlay.propTypes = {
  toggleVisible: PropTypes.func.isRequired,
  productID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  showQuestion: PropTypes.bool,
  isPDP: PropTypes.bool,
  lineID: PropTypes.number
};

CustomizationOverlay.defaultProps = {
  showQuestion: false,
  isPDP: false,
  lineID: undefined
};

export default CustomizationOverlay;
